import React from "react"
import PropTypes from "prop-types"
import { ReactComponent as AlertIcon } from "@ciq/shared/assets/icons/alert_icon.svg"

/**
 * @typedef {Object} Warning
 * @property {string} type - Warning type: "unmapped_room", "zero_capacity_zone", or "zero_sqft_zone"
 * @property {string} [room] - Room name for unmapped_room warnings
 * @property {number} [count] - Count for aggregate warnings
 */

/**
 * Format a warning object into a user-readable message.
 *
 * @param {Warning} warning - The warning object to format
 * @returns {string} User-friendly warning message
 */
function formatWarningMessage (warning) {
  switch (warning.type) {
    case "unmapped_room":
      return warning.room
        ? `Room "${warning.room}" could not be mapped to a zone`
        : "A room could not be mapped to a zone"
    case "zero_capacity_zone":
      return `${warning.count} ${warning.count === 1 ? "zone has" : "zones have"} zero capacity`
    case "zero_sqft_zone":
      return `${warning.count} ${warning.count === 1 ? "zone has" : "zones have"} zero square footage`
    default:
      return `Warning: ${warning.type}`
  }
}

/**
 * Generate a unique key for a warning item.
 * Always includes index to guarantee uniqueness even when warnings have identical values.
 *
 * @param {Warning} warning - The warning object
 * @param {number} index - Array index for uniqueness
 * @returns {string} Unique key for React reconciliation
 */
function getWarningKey (warning, index) {
  return `${warning.type}-${warning.room || warning.count || ""}-${index}`
}

/**
 * @typedef {Object} WarningsPanelProps
 * @property {Array<Warning>} warnings - Array of warning objects from processing results
 */

/**
 * Warning panel displaying non-blocking issues from zone data processing.
 * Shows amber-styled alerts for unmapped rooms and data quality issues.
 * Returns null if there are no warnings to display.
 *
 * @param {WarningsPanelProps} props - Component props
 * @returns {React.ReactElement|null} The warnings panel, or null if no warnings
 */
export function WarningsPanel ({ warnings }) {
  if (!warnings || warnings.length === 0) {
    return null
  }

  return (
    <div className="warnings-panel">
      <div className="warnings-panel__header">
        <AlertIcon className="warnings-panel__icon"/>
        <h3 className="warnings-panel__title">Warnings Found</h3>
      </div>
      <div className="warnings-panel__container">
        <ul className="warnings-panel__list">
          {warnings.map((warning, index) => (
            <li key={getWarningKey(warning, index)} className="warnings-panel__item">
              {formatWarningMessage(warning)}
            </li>
          ))}
        </ul>
        <p className="warnings-panel__help-text">
          These warnings will not block the upload. Review and proceed if the data is acceptable.
        </p>
      </div>
    </div>
  )
}

WarningsPanel.propTypes = {
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      room: PropTypes.string,
      count: PropTypes.number
    })
  )
}
